import React, { Dispatch, SetStateAction, useMemo } from 'react'
import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'
import { useFeatureFlags } from '@node-space/hooks'
import {
  Box as Container,
  CopyButton,
  DataTable,
  DataTableCellVariants,
  DataTableHeaderProps,
  EmptyState,
  Highlight,
  Pagination,
  Box as Row,
  RowsPerPageType,
  RowsSkeletonLoader,
  SidePanel,
  Tooltip,
  useSidePanel,
} from '@node-space/ui-components'
import { ellipseMiddleText } from '@node-space/utils'
import { Lottie } from 'components/Animations/ThemeLottie'
import { appBannersId } from 'components/app/AppBanners'
import CopyTextWrapper from 'components/CopyTextWrapper'
import { DateFormat } from 'constants/General'
import { MIN_PAGINATION_SIZE } from 'constants/queries'
import { PaymentCaseProvider } from 'contexts/paymentCaseContext'
import { useComponentHeight } from 'hooks/useComponentHeight'
import { CasesPagination, PaymentCase } from 'types/payments'
import type { Paging } from 'types/types'
import { highlightRowBasedOnId } from 'utils/table/table'
import { getPaymentCaseStatus, getPaymentCaseStatusLabel } from 'utils/utils'
import { CustomerIdInput } from './CustomerIdInput'
import { PaymentCaseDetailsBody } from './PaymentCaseDetailsBody'
import { PaymentCaseError } from './PaymentCaseError'

interface PaymentSupportTableProps {
  isFetching: boolean
  isError: boolean
  casesList: PaymentCase[]
  paging: Paging
  pagingMax: number
  setCaseListPagination: Dispatch<SetStateAction<CasesPagination>>
  onPageChange: ({ paging }: { paging: Paging }) => void
}

export const PaymentSupportTable = ({
  isFetching,
  isError,
  casesList,
  paging,
  pagingMax,
  setCaseListPagination,
  onPageChange,
}: PaymentSupportTableProps) => {
  const { t } = useTranslation()
  const { enableCasePortalCustomerId, enableCasePortalSupportTableUpdate } = useFeatureFlags()
  const bannerSpacing = useComponentHeight(appBannersId)
  const {
    isSidePanelOpen: isPaymentCaseOpen,
    sidePanelData: paymentCase,
    paging: pagingPaymentCases,
    setIsSidePanelOpen: setIsPaymentCaseOpen,
    updateSidePanel: updatePaymentCase,
  } = useSidePanel<PaymentCase>(casesList)

  const currentPage = Math.floor((paging?.offset ?? 0) / pagingMax)
  const totalItems = paging?.total ?? 0

  const handleChangePage = (newPage: number) => {
    const offset = newPage * pagingMax
    onPageChange({
      paging: {
        ...paging,
        offset,
        max: pagingMax,
      },
    })
  }

  const handleChangeRowsPerPage = (newRowsPerPage: RowsPerPageType) => {
    setCaseListPagination(prev => ({
      ...prev,
      paging: {
        ...prev.paging,
        offset: 0,
        max: newRowsPerPage,
      },
      pagingMax: newRowsPerPage,
    }))
    onPageChange({
      paging: {
        ...paging,
        offset: 0,
        max: newRowsPerPage,
      },
    })
  }

  const getValidRowsPerPage = (value: number): RowsPerPageType => {
    const validSizes: RowsPerPageType[] = [10, 20, 30, 40, 50, 100]
    return validSizes.includes(value as RowsPerPageType) ? (value as RowsPerPageType) : 20
  }

  const columns: DataTableHeaderProps[] = useMemo(() => {
    return [
      {
        key: 'paymentReference',
        label: t('paymentSupport.paymentReference'),
        variant: DataTableCellVariants.TEXT,
      },
      {
        key: 'customerId',
        label: t('paymentSupport.customerId'),
        variant: DataTableCellVariants.TEXT,
      },
      {
        key: 'subject',
        label: t('paymentSupport.subject'),
        variant: DataTableCellVariants.TEXT,
      },
      {
        key: 'caseOpened',
        label: t('paymentSupport.caseOpened'),
        variant: DataTableCellVariants.TEXT,
      },
      {
        key: 'status',
        label: t('paymentSupport.status'),
        variant: DataTableCellVariants.HIGHLIGHT,
      },
      {
        key: 'completed',
        label: t('paymentSupport.completed'),
        variant: DataTableCellVariants.TEXT,
      },
    ]
  }, [enableCasePortalSupportTableUpdate, enableCasePortalCustomerId])

  const setSidePanel = (index: number) => {
    updatePaymentCase(index)
    setIsPaymentCaseOpen(true)
  }

  const mappedPaymentCases = useMemo(() => {
    return casesList?.map((paymentCase, paymentCaseIndex) => {
      return {
        id: paymentCase?.caseNumber,
        testid: paymentCase?.caseNumber,
        onRowClick: () => setSidePanel(paymentCaseIndex),
        cells: [
          {
            key: 'paymentReference',
            variant: DataTableCellVariants.TEXT,
            Text: paymentCase?.paymentReference,

            ...(!!paymentCase?.paymentReference
              ? {
                  Actions: [
                    <CopyButton
                      key={`copy-${paymentCase?.caseNumber}`}
                      copyText={paymentCase?.paymentReference}
                    />,
                  ],
                }
              : {}),
          },
          {
            key: 'customerId',
            variant: DataTableCellVariants.TEXT,
            Text: paymentCase?.customerId,
          },
          {
            key: 'subject',
            variant: DataTableCellVariants.TEXT,
            Text: paymentCase?.subject,
          },
          {
            key: 'caseOpened',
            variant: DataTableCellVariants.TEXT,
            Text: dayjs(paymentCase?.createdDate).format(DateFormat.DD_MMM_YYYY),
          },
          {
            key: 'status',
            variant: DataTableCellVariants.HIGHLIGHT,
            Highlights: [
              <Highlight
                key={`${paymentCase?.caseNumber}-status`}
                type={getPaymentCaseStatus(paymentCase?.status)}
              >
                {getPaymentCaseStatusLabel(paymentCase?.status)}
              </Highlight>,
            ],
          },
          {
            key: 'completed',
            variant: DataTableCellVariants.HIGHLIGHT,
            ...(paymentCase?.customerCaseCompleted
              ? {
                  Highlights: [
                    <Highlight key={`${paymentCase?.caseNumber}-completed`} type="success">
                      {t('paymentSupport.completed')}
                    </Highlight>,
                  ],
                }
              : {}),
          },
        ],
      }
    })
  }, [casesList, enableCasePortalSupportTableUpdate, enableCasePortalCustomerId])

  return (
    <>
      {/* Show content and loading state */}
      {(isFetching || (!isError && !!casesList?.length)) && (
        <>
          {isFetching ? (
            <RowsSkeletonLoader rows={MIN_PAGINATION_SIZE} />
          ) : (
            <>
              <DataTable headers={columns} data={mappedPaymentCases} />

              <Pagination
                testId="payment-support-pagination"
                page={currentPage}
                rowsPerPage={getValidRowsPerPage(pagingMax)}
                total={totalItems}
                onChangePage={handleChangePage}
                onChangeRowsPerPage={handleChangeRowsPerPage}
              />
            </>
          )}

          <SidePanel
            id={'side-panel-payment-support'}
            isOpen={isPaymentCaseOpen}
            onClose={() => setIsPaymentCaseOpen(false)}
            topOffset={bannerSpacing}
            paging={pagingPaymentCases}
            activeRowId={paymentCase?.caseNumber}
            onActiveRowChange={highlightRowBasedOnId}
            header={
              <Container flexCol gapY={4}>
                <Row flex align="center" justify="between">
                  <h3 className="text-h3 text-foreground-prominent sm:text-xl">
                    {`${t('paymentSupport.caseNumber').toUpperCase()} ${paymentCase?.caseNumber}`}
                  </h3>
                </Row>
                <Row>
                  {!paymentCase?.paymentReference ? (
                    <Tooltip bodyContent={t('paymentSupport.nullTooltip')} />
                  ) : (
                    <CopyTextWrapper
                      textToCopy={paymentCase?.paymentReference}
                      textContainer={
                        <p className="body-regular-text text-foreground-prominent">
                          {ellipseMiddleText(paymentCase?.paymentReference, 7, 7)}
                        </p>
                      }
                    />
                  )}
                </Row>
                <Row>
                  <Highlight type={getPaymentCaseStatus(paymentCase?.status)}>
                    {getPaymentCaseStatusLabel(paymentCase?.status)}
                  </Highlight>
                </Row>
                <Row>
                  {enableCasePortalCustomerId ? (
                    <CustomerIdInput
                      customerId={paymentCase?.customerId}
                      caseId={paymentCase?.id}
                    />
                  ) : (
                    <p className="body-regular-text text-foreground-subdued">
                      {paymentCase?.subject}
                    </p>
                  )}
                </Row>
              </Container>
            }
            body={
              <PaymentCaseProvider
                paymentCaseId={paymentCase?.id}
                paymentCaseStatus={paymentCase?.status}
              >
                <PaymentCaseDetailsBody />
              </PaymentCaseProvider>
            }
          ></SidePanel>
        </>
      )}

      {/* Show empty state */}
      {!isFetching && !isError && !casesList?.length && (
        <EmptyState
          testId="empty-state"
          LottiePlayer={<Lottie src="no-data" />}
          title={
            !status?.length
              ? t('paymentSupport.noCasesOpened')
              : t('paymentSupport.noCasesOpenedOnFilter')
          }
          description={t('paymentSupport.complianceTeamNotice')}
        />
      )}

      {/* Show error state */}
      {isError && !isFetching && (
        <EmptyState
          testId="error-state"
          LottiePlayer={<Lottie src="connection-error" />}
          title={t('paymentSupport.errorHeadline')}
          description={<PaymentCaseError />}
        />
      )}
    </>
  )
}
